import { arrayToMap } from 'lib/array/arrayUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import type { TripDocumentsResult } from 'tripPlanner/hooks/api/documents'
import type { FullTrip } from 'tripPlanner/types/common'
import type { TripDocumentWithItemInfo } from 'tripPlanner/types/documents'
import type { TripItem } from 'tripPlanner/types/tripItem'

interface DocumentDisplayLabels {
  flightLabel: string
  noteLabel: string
}

function getDefaultLabels(): DocumentDisplayLabels {
  const intl = getCurrentIntl()

  return {
    flightLabel: intl.formatMessage({
      id: '9QSd9W2d724V78T3j7WC',
      defaultMessage: 'flight',
      description: 'Fallback label appended to airport codes for flight items in trip documents',
    }),
    noteLabel: intl.formatMessage({
      id: 'XCHiswhPMT3_H9_mfyoL',
      defaultMessage: 'Note',
      description: 'Fallback label for untitled note items in trip documents when no explicit labels are passed',
    }),
  }
}

export function flattenDocuments(
  trip: FullTrip,
  documents: TripDocumentsResult,
  labels?: DocumentDisplayLabels,
): Array<TripDocumentWithItemInfo> {
  const indexedItems = arrayToMap(trip.items, (item) => item.id)
  const attachedFiles = Object.entries(documents.attachedFiles).flatMap(
    ([itemId, files]) => {
      const item = indexedItems.get(itemId)
      if (item) {
        return files.map((file) => ({
          ...file,
          item: { id: item.id, name: getAttachedItemDisplayName(item, labels) },
        }))
      }
      return files
    },
  )
  return [...attachedFiles, ...documents.unAttachedFiles]
}

export function getAttachedItemDisplayName(item: TripItem, labels: DocumentDisplayLabels = getDefaultLabels()): string {
  if (item.type === 'FLIGHT') {
    return `${item.originAirportCode} - ${item.destinationAirportCode} ${labels.flightLabel}`
  }
  if (item.type === 'NOTE') {
    return item.title || labels.noteLabel
  }
  if ('name' in item && item.name) {
    return item.name
  }
  if ('place' in item && item.place.name) {
    return item.place.name
  }
  return item.sortableName
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
